/**
 * @file Helper functions for message permission checks
 */

import { User } from '../../generated/graphql';
import { MessageAssigned, MessageCreator } from '../../models/message';
import { PermissionsMessage } from '../../models/permissions';

import { MESSAGE__ADMIN } from './permissionsAll';
import { getPermissionsCurrent, getUserCurrent } from './storage';

/**
 * Check whether the user has a specific permission
 *
 * @param which Which permission we want to check against
 * @returns     Whether it's true
 */
export const hasPermission = (which: PermissionsMessage): boolean => {
  const permissions = getPermissionsCurrent();
  return permissions.includes(which);
};

/**
 * Check whether the current user has admin permissions for message
 *
 * @returns Whether it's true
 */
export const getIsAdmin = (): boolean => {
  return getPermissionsCurrent().includes(MESSAGE__ADMIN);
};

/**
 * Check whether the user is either directly assigned to the message,
 * or is a member of a group that is assigned to it
 *
 * @param message                 Message to check
 * @param message.assignedGroups  Assigned user groups to the message
 * @param message.assignedMembers Assigned members/users to the message
 * @returns                       Whether the user is assigned to the message
 */
export const getIsAssigned = ({
  assignedGroups,
  assignedMembers,
}: MessageAssigned): boolean => {
  const user = getUserCurrent();

  // Permissions not initialized yet
  if (user === null) {
    return false;
  }

  const userId = user.id;
  /**
   * Check whether the passed member is the current user
   *
   * @param member The member to check
   * @returns      Whether it's true
   */
  const getIsMember = (member: Pick<User, 'id'>) => member.id === userId;

  // Whether the user is assigned to the message
  const isMemberAssigned = assignedMembers.findIndex(getIsMember) !== -1;
  if (isMemberAssigned) {
    return true;
  }

  // If not, check whether the user is a member of a group
  // that is assigned to the message
  return assignedGroups.some(
    group => group.members.findIndex(getIsMember) !== -1,
  );
};

/**
 * Check whether the current user is the message author
 *
 * @param message The message to check permissions for
 * @returns       Whether it's true
 */
export const getIsAuthor = (message: MessageCreator): boolean => {
  // Will happen for auto-generated topics
  // Which will happen when concierge form is posted 1st time
  if (message.creator === null || message.creator === undefined) {
    return false;
  }
  const user = getUserCurrent();
  return user !== null && user.id === message.creator.id;
};
